import React, {useRef, useCallback, useState, useEffect} from 'react';
import {Icon} from '@albert-io/atomic';
import {ANNOTATION_SHIELD_EXEMPT_PROPS} from 'client/components/PracticeView/Annotations/shared/annotationFreeze';
import {ANNOTATE_IGNORE_PROPS} from 'client/components/PracticeView/Annotations/engine/annotationTextProjection';

import './custom-scrollbar.scss';
import classNames from 'classnames';

interface CustomScrollbarProps {
  children: React.ReactNode;
  className?: string;
  isSmall?: boolean;
}

const CustomScrollbar = ({children, className, isSmall}: CustomScrollbarProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumbHeight, setThumbHeight] = useState(50);
  const [thumbTop, setThumbTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartScrollTop, setDragStartScrollTop] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateThumb = useCallback(() => {
    if (contentRef.current) {
      const {scrollTop, scrollHeight, clientHeight} = contentRef.current;

      // Check if content is overflowing
      const overflowing = scrollHeight > clientHeight;
      setIsOverflowing(overflowing);

      // Only calculate thumb position if track exists and we're overflowing
      if (trackRef.current && overflowing) {
        const trackHeight = trackRef.current.clientHeight;

        // Calculate thumb height as a percentage of visible content
        const visibleRatio = clientHeight / scrollHeight;
        const newThumbHeight = Math.max(visibleRatio * trackHeight, 40);
        setThumbHeight(newThumbHeight);

        // Calculate thumb position
        const maxScroll = scrollHeight - clientHeight;
        const scrollRatio = maxScroll > 0 ? scrollTop / maxScroll : 0;
        const maxThumbTop = trackHeight - newThumbHeight;
        setThumbTop(scrollRatio * maxThumbTop);
      }
    }
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', updateThumb);
      // Initial calculation
      updateThumb();

      // Recalculate on resize and content changes
      const resizeObserver = new ResizeObserver(updateThumb);
      resizeObserver.observe(content);

      // Also observe all children for changes
      Array.from(content.children).forEach((child) => {
        resizeObserver.observe(child);
      });

      // Use MutationObserver to detect when children are added/removed
      const mutationObserver = new MutationObserver(() => {
        updateThumb();
        // Re-observe new children
        Array.from(content.children).forEach((child) => {
          resizeObserver.observe(child);
        });
      });

      mutationObserver.observe(content, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });

      return () => {
        content.removeEventListener('scroll', updateThumb);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }
  }, [updateThumb]);

  const scrollUp = useCallback(() => {
    if (contentRef.current && isOverflowing) {
      contentRef.current.scrollBy({top: -100, behavior: 'smooth'});
    }
  }, [isOverflowing]);

  const scrollDown = useCallback(() => {
    if (contentRef.current && isOverflowing) {
      contentRef.current.scrollBy({top: 100, behavior: 'smooth'});
    }
  }, [isOverflowing]);

  const handleThumbMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isOverflowing) return;
      e.preventDefault();
      setIsDragging(true);
      setDragStartY(e.clientY);
      setDragStartScrollTop(contentRef.current?.scrollTop || 0);
    },
    [isOverflowing]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !contentRef.current || !trackRef.current) return;

      const deltaY = e.clientY - dragStartY;
      const trackHeight = trackRef.current.clientHeight;
      const {scrollHeight, clientHeight} = contentRef.current;
      const maxScroll = scrollHeight - clientHeight;
      const scrollRatio = deltaY / (trackHeight - thumbHeight);

      contentRef.current.scrollTop = dragStartScrollTop + scrollRatio * maxScroll;
    },
    [isDragging, dragStartY, dragStartScrollTop, thumbHeight]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (
        !contentRef.current ||
        !trackRef.current ||
        e.target === thumbRef.current ||
        !isOverflowing
      )
        return;

      const trackRect = trackRef.current.getBoundingClientRect();
      const clickY = e.clientY - trackRect.top;
      const trackHeight = trackRef.current.clientHeight;
      const {scrollHeight, clientHeight} = contentRef.current;
      const maxScroll = scrollHeight - clientHeight;

      const scrollRatio = clickY / trackHeight;
      contentRef.current.scrollTo({
        top: scrollRatio * maxScroll,
        behavior: 'smooth'
      });
    },
    [isOverflowing]
  );

  return (
    <div className={`custom-scrollbar ${className || ''}`}>
      <div
        className={classNames('custom-scrollbar__content', {
          'py-x1!': isSmall
        })}
        ref={contentRef}
      >
        {children}
      </div>
      {isOverflowing && (
        <div
          className='custom-scrollbar__track-wrapper'
          role='presentation'
          {...ANNOTATE_IGNORE_PROPS}
          {...ANNOTATION_SHIELD_EXEMPT_PROPS}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            className='custom-scrollbar__button custom-scrollbar__button--up'
            onClick={scrollUp}
            aria-label='Scroll up'
            disabled={!isOverflowing}
          >
            <Icon icon='chevron-up' color='primary-inverse-static' />
          </button>
          <div
            className='custom-scrollbar__track'
            ref={trackRef}
            onClick={handleTrackClick}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                scrollUp();
              } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                scrollDown();
              } else if (e.key === 'PageUp') {
                e.preventDefault();
                contentRef.current?.scrollBy({top: -300, behavior: 'smooth'});
              } else if (e.key === 'PageDown') {
                e.preventDefault();
                contentRef.current?.scrollBy({top: 300, behavior: 'smooth'});
              } else if (e.key === 'Home') {
                e.preventDefault();
                contentRef.current?.scrollTo({top: 0, behavior: 'smooth'});
              } else if (e.key === 'End') {
                e.preventDefault();
                contentRef.current?.scrollTo({
                  top: contentRef.current.scrollHeight,
                  behavior: 'smooth'
                });
              }
            }}
            role='scrollbar'
            aria-controls='scrollable-content'
            aria-valuenow={thumbTop}
            tabIndex={-1}
          >
            <div
              className='custom-scrollbar__thumb'
              ref={thumbRef}
              style={{
                height: `${thumbHeight}px`,
                transform: `translateY(${thumbTop}px)`
              }}
              onMouseDown={handleThumbMouseDown}
            />
          </div>
          <button
            type='button'
            className='custom-scrollbar__button custom-scrollbar__button--down'
            onClick={scrollDown}
            aria-label='Scroll down'
            disabled={!isOverflowing}
          >
            <Icon icon='chevron-down' color='primary-inverse-static' />
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomScrollbar;
